// Ensure API_BASE_URL doesn't have trailing slash and includes /api
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;

// Log the API base URL on load (for debugging)
console.log('API_BASE_URL:', API_BASE_URL);

// Request cache for GET requests (simple in-memory cache)
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to handle API responses with retry logic
const handleResponse = async (response, retries = 0) => {
  if (!response.ok) {
    // Retry on network errors or 5xx errors
    if ((!response.status || response.status >= 500) && retries < 3) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retries + 1)));
      // Note: This is a simplified retry. In production, you'd want to retry the actual request
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || 'Something went wrong');
    }
    
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    console.error('API Error:', response.status, error);
    throw new Error(error.error || `Request failed with status ${response.status}`);
  }
  return response.json();
};

// Helper function to format dates consistently
const formatDate = (date) => {
  if (!date) return date;
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  return date;
};

// Helper function for GET requests with caching
const getWithCache = async (url, useCache = true) => {
  const cacheKey = url;
  const cached = requestCache.get(cacheKey);
  
  if (useCache && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  try {
    console.log('Fetching:', url);
    const response = await fetch(url);
    console.log('Response status:', response.status, 'for', url);
    const data = await handleResponse(response);
    
    if (useCache) {
      requestCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }
    
    return data;
  } catch (error) {
    console.error('Fetch error for', url, ':', error.message);
    // Return cached data if available even if request fails
    if (cached) {
      console.warn('Using cached data due to request failure:', error);
      return cached.data;
    }
    // Add more context to network errors
    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      throw new Error(`Cannot connect to backend server at ${API_BASE_URL}. Please make sure the backend is running on port 5000.`);
    }
    throw error;
  }
};

// Clear cache function
export const clearAPICache = () => {
  requestCache.clear();
};

// Entries API
export const entriesAPI = {
  getAll: async (useCache = true) => {
    return getWithCache(`${API_BASE_URL}/entries`, useCache);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/entries/${id}`);
    return handleResponse(response);
  },

  create: async (entry) => {
    const response = await fetch(`${API_BASE_URL}/entries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...entry,
        date: formatDate(entry.date)
      }),
    });
    return handleResponse(response);
  },

  update: async (id, entry) => {
    const response = await fetch(`${API_BASE_URL}/entries/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...entry,
        date: formatDate(entry.date)
      }),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/entries/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  deleteAll: async () => {
    const response = await fetch(`${API_BASE_URL}/entries`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Customer Credits API
export const creditsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/credits`);
    return handleResponse(response);
  },

  getByEntry: async (date, group, time_group) => {
    const dateStr = formatDate(date);
    const response = await fetch(
      `${API_BASE_URL}/credits/by-entry?date=${dateStr}&group=${encodeURIComponent(group)}&time_group=${encodeURIComponent(time_group)}`
    );
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/credits/${id}`);
    return handleResponse(response);
  },

  create: async (credit) => {
    const response = await fetch(`${API_BASE_URL}/credits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...credit,
        date: formatDate(credit.date)
      }),
    });
    return handleResponse(response);
  },

  update: async (id, credit) => {
    const response = await fetch(`${API_BASE_URL}/credits/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...credit,
        date: formatDate(credit.date)
      }),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/credits/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  deleteAll: async () => {
    const response = await fetch(`${API_BASE_URL}/credits`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Customers API
export const customersAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/customers`);
    return handleResponse(response);
  },

  search: async (query) => {
    const response = await fetch(`${API_BASE_URL}/customers/search?q=${encodeURIComponent(query)}`);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`);
    return handleResponse(response);
  },

  getCredits: async (id) => {
    const response = await fetch(`${API_BASE_URL}/customers/${id}/credits`);
    return handleResponse(response);
  },

  getCreditsSummary: async () => {
    const response = await fetch(`${API_BASE_URL}/customers/summary/credits`);
    return handleResponse(response);
  },

  create: async (customer) => {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(customer),
    });
    return handleResponse(response);
  },

  update: async (id, customer) => {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(customer),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Payments API
export const paymentsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/payments`);
    return handleResponse(response);
  },

  getByCustomer: async (customerName) => {
    const response = await fetch(`${API_BASE_URL}/payments/customer/${encodeURIComponent(customerName)}`);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/payments/${id}`);
    return handleResponse(response);
  },

  getBalance: async (customerName) => {
    const response = await fetch(`${API_BASE_URL}/payments/balance/${encodeURIComponent(customerName)}`);
    return handleResponse(response);
  },

  create: async (payment) => {
    const response = await fetch(`${API_BASE_URL}/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payment,
        date: formatDate(payment.date)
      }),
    });
    return handleResponse(response);
  },

  update: async (id, payment) => {
    const response = await fetch(`${API_BASE_URL}/payments/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payment,
        date: formatDate(payment.date)
      }),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/payments/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Employee Credits API
export const employeeCreditsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/employee-credits`);
    return handleResponse(response);
  },

  getByEntry: async (date, group, time_group) => {
    const dateStr = formatDate(date);
    const response = await fetch(
      `${API_BASE_URL}/employee-credits/by-entry?date=${dateStr}&group=${encodeURIComponent(group)}&time_group=${encodeURIComponent(time_group)}`
    );
    return handleResponse(response);
  },

  getByEmployee: async (employeeName) => {
    const response = await fetch(`${API_BASE_URL}/employee-credits/employee/${encodeURIComponent(employeeName)}`);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employee-credits/${id}`);
    return handleResponse(response);
  },

  create: async (credit) => {
    const response = await fetch(`${API_BASE_URL}/employee-credits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...credit,
        date: formatDate(credit.date)
      }),
    });
    return handleResponse(response);
  },

  update: async (id, credit) => {
    const response = await fetch(`${API_BASE_URL}/employee-credits/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...credit,
        date: formatDate(credit.date)
      }),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employee-credits/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  deleteAll: async () => {
    const response = await fetch(`${API_BASE_URL}/employee-credits`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Employees API
export const employeesAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/employees`);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`);
    return handleResponse(response);
  },

  getConsumption: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}/consumption`);
    return handleResponse(response);
  },

  getConsumptionSummary: async (year, month) => {
    let url = `${API_BASE_URL}/employees/summary/consumption`;
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  create: async (employee) => {
    const response = await fetch(`${API_BASE_URL}/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(employee),
    });
    return handleResponse(response);
  },

  update: async (id, employee) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(employee),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  createConsumption: async (consumption) => {
    const response = await fetch(`${API_BASE_URL}/employees/consumption`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consumption),
    });
    return handleResponse(response);
  },

  updateConsumption: async (id, consumption) => {
    const response = await fetch(`${API_BASE_URL}/employees/consumption/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consumption),
    });
    return handleResponse(response);
  },

  deleteConsumption: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/consumption/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Settings API
export const settingsAPI = {
  get: async () => {
    const response = await fetch(`${API_BASE_URL}/settings`);
    return handleResponse(response);
  },

  update: async (settings) => {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });
    return handleResponse(response);
  },
};

